/**
 * Route gate. Redirects unauthenticated users to /signin and
 * mismatched roles to their landing page.
 *
 * Per spec 001 FR-006:
 *   - customer        → /dashboard
 *   - tenant_admin    → /admin/queue
 *   - platform_admin  → /admin/platform/tenants
 */

import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import type { Role } from "@starz/shared/api/types.gen";
import { useAuthRole, useAuthStatus } from "@/lib/auth/useAuth";

interface ProtectedRouteProps {
  /** Roles allowed to view the children. Empty array means any
   * authenticated role. */
  allowedRoles: Role[];
  children: ReactNode;
}

const ROLE_LANDING: Record<Role, string> = {
  customer: "/dashboard",
  tenant_admin: "/admin/queue",
  platform_admin: "/admin/platform/tenants",
};

export function ProtectedRoute({ allowedRoles, children }: ProtectedRouteProps) {
  const status = useAuthStatus();
  const role = useAuthRole();
  const location = useLocation();

  if (status === "unknown") {
    return <div className="grid min-h-screen place-items-center text-warm-gray">Loading…</div>;
  }

  if (status !== "authenticated" || !role) {
    return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(role)) {
    return <Navigate to={ROLE_LANDING[role]} replace />;
  }

  return <>{children}</>;
}
